import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { Avatar } from '../../atoms/Avatar/Avatar.js';
import { cn } from '../../lib/cn.js';
import { FOCUS_RING } from '../../lib/focus-ring.js';
import { OVERLAY_SURFACE } from '../../lib/surface-styles.js';
/**
 * Profile menu (handoff, Molecules).
 *
 * Written out rather than pulled from a menu library. It is one button and
 * three items, and the behaviour that matters is small enough to read: arrows
 * move, Home and End jump, Esc closes and gives focus back to the trigger,
 * clicking elsewhere closes. A dependency for this would be more supply chain
 * than component.
 */
export function ProfileMenu({ name, email, avatarUrl, items, groups = [], className, }) {
    const menuId = useId();
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const itemRefs = useRef([]);
    useEffect(() => {
        if (!open)
            return;
        function onPointerDown(event) {
            if (!containerRef.current?.contains(event.target))
                setOpen(false);
        }
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [open]);
    useEffect(() => {
        if (open)
            itemRefs.current[activeIndex]?.focus();
    }, [open, activeIndex]);
    /**
     * The items in the order they are drawn, which is the order the arrow keys
     * move through.
     *
     * Grouping is presentational, so it must not change the keyboard order — a
     * menu where Down goes somewhere other than the next visible item is worse
     * than an ungrouped one. One sequence, used for both.
     */
    const ungrouped = items.filter((item) => item.group === undefined);
    const ordered = [
        ...ungrouped,
        ...groups.flatMap((group) => items.filter((item) => item.group === group.id)),
    ];
    function close(returnFocus) {
        setOpen(false);
        if (returnFocus)
            triggerRef.current?.focus();
    }
    function onMenuKeyDown(event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            close(true);
            return;
        }
        if (event.key === 'Tab') {
            // Tabbing out is a decision to leave, so it closes but does not yank
            // focus back to the trigger.
            close(false);
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((index) => (index + 1) % ordered.length);
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((index) => (index - 1 + ordered.length) % ordered.length);
        }
        if (event.key === 'Home') {
            event.preventDefault();
            setActiveIndex(0);
        }
        if (event.key === 'End') {
            event.preventDefault();
            setActiveIndex(ordered.length - 1);
        }
    }
    function renderItem(item) {
        const index = ordered.indexOf(item);
        const selectable = item.selected !== undefined;
        return (_jsxs("button", { ref: (element) => {
                itemRefs.current[index] = element;
            }, type: "button", role: selectable ? 'menuitemradio' : 'menuitem', ...(selectable ? { 'aria-checked': item.selected } : {}), tabIndex: index === activeIndex ? 0 : -1, onClick: () => {
                close(true);
                item.onSelect();
            }, className: cn('flex w-full items-center gap-2 rounded-sm px-[18px] py-3.5 text-left', 'text-body-sm text-text transition-colors duration-fast', 'hover:bg-surface-secondary', item.selected && 'font-semibold', FOCUS_RING), children: [selectable ? (_jsx("span", { "aria-hidden": "true", className: "w-3 shrink-0 text-primary", children: item.selected ? '✓' : '' })) : null, item.label] }, item.id));
    }
    return (_jsxs("div", { ref: containerRef, className: cn('relative', className), children: [_jsxs("button", { ref: triggerRef, type: "button", "aria-haspopup": "menu", "aria-expanded": open, onClick: () => {
                    setActiveIndex(0);
                    setOpen((wasOpen) => !wasOpen);
                }, onKeyDown: (event) => {
                    if (event.key === 'ArrowDown' && !open) {
                        event.preventDefault();
                        setActiveIndex(0);
                        setOpen(true);
                    }
                }, className: cn('flex h-control-md items-center gap-2 rounded-sm px-2', 
                // The touch floor on a phone, where this is pressed with a thumb;
                // the control scale from the tablet up.
                'max-md:h-touch-target-min', 'text-body font-medium text-text transition-colors duration-fast', 'hover:bg-surface-secondary', FOCUS_RING), children: [_jsx(Avatar, { name: name, size: "sm", src: avatarUrl }), _jsx("span", { className: "sr-only desktop:not-sr-only", children: name }), _jsx(ChevronDown, { "aria-hidden": "true", className: "size-icon-sm text-text-muted" })] }), open ? (_jsxs("div", { role: "menu", "aria-label": email ? `${name} (${email})` : name, onKeyDown: onMenuKeyDown, className: cn(OVERLAY_SURFACE, 'absolute right-0 z-10 mt-2 w-56 p-2.5'), children: [email ? (_jsx("p", { className: "truncate px-[18px] pb-2 pt-1 text-caption text-text-muted", children: email })) : null, ungrouped.map((item) => renderItem(item)), groups.map((group) => {
                        const members = items.filter((item) => item.group === group.id);
                        if (members.length === 0)
                            return null;
                        return (_jsxs("div", { role: "group", "aria-labelledby": `${menuId}-${group.id}`, children: [_jsx("p", { id: `${menuId}-${group.id}`, className: "px-[18px] pb-1 pt-3 text-caption font-semibold uppercase tracking-wide text-text-muted", children: group.label }), members.map((item) => renderItem(item))] }, group.id));
                    })] })) : null] }));
}
//# sourceMappingURL=ProfileMenu.js.map